import { useCallback, useEffect, useState } from 'react';
import type { UserRole } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiGet, apiPut, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { BeoordeelLijst } from '../components/declaraties/BeoordeelLijst.js';

/**
 * Beheer Declaraties — de enige wachtrij (besluit Jos 10 sep 2026): voor
 * teamleiders (eigen team), kantoorpersoneel en admins (alles), plus het
 * toewijzingspaneel waarmee Linda (suppohrt_admin) per medewerker de
 * teamleider kiest.
 */
export function BeheerDeclaratiesPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'suppohrt_admin';

  // Zelfde poort als het sidebar-item (nav-visibility): een gewone medewerker
  // die de URL direct opent mag hier geen beheer-call doen — die zou een 403
  // mét access.refused-auditregel opleveren, en die regels voeden de
  // incident-signaal-janitor.
  if (user && !isAdmin && user.isHeadOffice !== true && user.isTeamleider !== true) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-5">
          <h1 className="text-xl font-bold text-ink-900">Beheer Declaraties</h1>
          <p className="mt-3 rounded-2xl border border-brand-100 bg-white p-5 text-[14px] text-ink-900/70">
            Deze pagina is voor teamleiders, kantoorpersoneel en beheerders.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-5">
        <h1 className="text-xl font-bold text-ink-900">Beheer Declaraties</h1>
        <p className="mt-1 text-[14px] text-ink-900/70">
          Beoordeel declaraties. Na akkoord gaan ze meteen per e-mail naar de administratie.
        </p>
        <div className="mt-5">
          <BeoordeelLijst />
        </div>
        {isAdmin && <TeamleiderPaneel />}
      </div>
    </Layout>
  );
}

interface TeamleiderRij {
  id: string;
  displayName: string;
  email: string;
  role: UserRole;
  isHeadOffice: boolean;
  isTeamleider: boolean;
  teamleiderUserId?: string | null;
}

/**
 * Teamleiderbeheer (besluit Jos 3 sep 2026): het vinkje markeert wíe
 * teamleider is; alleen gemarkeerde collega's zijn kiesbaar in de
 * zelf-service keuze op de declaratiepagina. Wie bij wie hoort, kiezen
 * medewerkers daar zelf — hier staat bewust geen toewijzing.
 */
function TeamleiderPaneel(): JSX.Element {
  const [rows, setRows] = useState<TeamleiderRij[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [bezigVoor, setBezigVoor] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');

  // Bij ~130 gebruikers is één platte tabel niet meer scanbaar en wordt het
  // vinkje van de verkeerde rij het reële risico. Daarom: client-side zoeken
  // (zelfde patroon als het Smoelenboek) en de gemarkeerde teamleiders
  // bovenaan, zodat de handvol die ertoe doet altijd in beeld staat.
  const term = zoek.trim().toLowerCase();
  const zichtbaar = rows
    .filter((r) => !term || `${r.displayName} ${r.email}`.toLowerCase().includes(term))
    .sort((a, b) =>
      a.isTeamleider !== b.isTeamleider
        ? Number(b.isTeamleider) - Number(a.isTeamleider)
        : a.displayName.localeCompare(b.displayName, 'nl'),
    );

  const load = useCallback((): void => {
    void apiGet<{ users: TeamleiderRij[] }>('/api/v1/beheer/declaraties/teamleiders')
      .then((d) => {
        setRows(d.users);
        setErrorMsg(null);
      })
      .catch((err) => {
        setErrorMsg(
          err instanceof ApiError
            ? `Toewijzingen laden mislukt (${err.status}): ${err.message}`
            : 'Toewijzingen laden mislukt.',
        );
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function markeer(userId: string, isTeamleider: boolean): Promise<void> {
    setBezigVoor(userId);
    setErrorMsg(null);
    try {
      await apiPut(`/api/v1/beheer/declaraties/teamleiders/${userId}/vlag`, { isTeamleider });
      load();
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? err.message : 'Markeren is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setBezigVoor(null);
    }
  }

  return (
    <section className="mt-10">
      <h2 className="text-[16px] font-bold text-ink-900">Teamleiders markeren</h2>
      <p className="mt-1 text-[14px] text-ink-900/70">
        Vink aan wie teamleider is. Medewerkers kiezen zelf hun teamleider op de declaratiepagina,
        uit alleen de aangevinkte collega&apos;s; gemarkeerde teamleiders staan hieronder bovenaan.
        Zonder (gemarkeerde) teamleider valt een declaratie direct terug op kantoor.
      </p>
      {errorMsg && (
        <p className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">{errorMsg}</p>
      )}
      <input
        type="search"
        value={zoek}
        onChange={(e) => setZoek(e.target.value)}
        placeholder="Zoek op naam of e-mail…"
        aria-label="Zoek medewerker"
        className="mt-3 w-full max-w-sm rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
      {term && (
        <p className="mt-2 text-[13px] text-ink-900/60">
          {zichtbaar.length} van {rows.length} medewerkers
        </p>
      )}
      <div className="mt-3 overflow-x-auto rounded-2xl border border-brand-100 bg-white">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="border-b border-brand-100 text-left text-[12px] uppercase tracking-wider text-ink-900/50">
              <th className="px-4 py-2 font-semibold">Medewerker</th>
              <th className="px-4 py-2 font-semibold">E-mail</th>
              <th className="px-4 py-2 font-semibold">Is teamleider</th>
            </tr>
          </thead>
          <tbody>
            {zichtbaar.map((rij) => (
              <tr key={rij.id} className="border-b border-brand-100/60 last:border-b-0">
                <td className="px-4 py-2 text-ink-900">{rij.displayName}</td>
                <td className="px-4 py-2 text-ink-900/60">{rij.email}</td>
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label={`${rij.displayName} is teamleider`}
                    checked={rij.isTeamleider}
                    disabled={bezigVoor === rij.id}
                    onChange={(e) => void markeer(rij.id, e.target.checked)}
                    className="h-4 w-4 accent-brand-600 disabled:opacity-50"
                  />
                </td>
              </tr>
            ))}
            {term !== '' && zichtbaar.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-3 text-ink-900/60">
                  Geen medewerkers gevonden voor &quot;{zoek}&quot;.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
